import { lazy, Suspense } from 'react';
import { styled } from '@mui/material/styles';
import {
  createBrowserRouter,
  createRoutesFromElements,
  isRouteErrorResponse,
  Navigate,
  Outlet,
  Route,
  useRouteError,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import FullscreenProgress from 'containers/AppStartup/FullScreenProgress';
import NotFound from 'components/NotFound';
import Home from 'containers/Dashboard/views/Home';
import Templates from 'containers/Dashboard/views/Templates';
import ApprovedFiles from 'containers/Dashboard/views/ApprovedFiles';
import Bin from 'containers/Dashboard/views/Bin';
import { initializeSentry } from 'utils/sentry';
import { initGA } from 'analytics/init';
import { FeatureFlag, useFeatureFlag } from 'hooks/useFeatureFlag';
import BetaFeaturesButton from 'containers/BetaFeatures/BetaFeaturesButton';
import { TourRegistryProvider } from 'tour/tourRegistry';

initGA();
initializeSentry();

const Designer = lazy(() => import('./containers/Designer'));
const Dashboard = lazy(() => import('./containers/Dashboard'));
const PlantDesignerChatbot = lazy(() => import('containers/Chatbot/Chatbot'));

const isDynamicImportFailure = (error: unknown) =>
  error instanceof TypeError &&
  error.message.includes('Failed to fetch dynamically imported module');

function RouteErrorFallback() {
  const error = useRouteError();

  if (isDynamicImportFailure(error)) {
    return (
      <ErrorState>
        <ErrorCard>
          <ErrorTitle>Could not load part of the app</ErrorTitle>
          <ErrorText>Part of the app could not be loaded correctly.</ErrorText>
          <ErrorText>
            Try a hard refresh. If the problem persists, check for blocked
            requests, browser extensions, or stale cached files and reload the
            page.
          </ErrorText>
          <ErrorActions>
            <ErrorButton onClick={() => window.location.reload()}>
              Reload
            </ErrorButton>
          </ErrorActions>
        </ErrorCard>
      </ErrorState>
    );
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />;
  }

  return (
    <ErrorState>
      <ErrorCard>
        <ErrorTitle>Something went wrong</ErrorTitle>
        <ErrorText>Please reload the page and try again.</ErrorText>
        <ErrorActions>
          <ErrorButton onClick={() => window.location.reload()}>
            Reload
          </ErrorButton>
        </ErrorActions>
      </ErrorCard>
    </ErrorState>
  );
}

function AppLayout() {
  const chatbotEnabled = useFeatureFlag(FeatureFlag.ENABLE_CHATBOT);

  return (
    <>
      <Outlet />
      <BetaFeaturesButton />
      {chatbotEnabled && <PlantDesignerChatbot />}
    </>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path='/'
      element={<AppLayout />}
      errorElement={<RouteErrorFallback />}
    >
      <Route path='files/:id' element={<Designer />} />
      <Route path='explorer' element={<Dashboard />}>
        <Route path={'approved-files'} element={<ApprovedFiles />} />
        <Route path={'templates'} element={<Templates />} />
        <Route path={'bin'} element={<Bin />} />
        <Route path={':id?'} element={<Home />} />
      </Route>
      <Route path='/' element={<Navigate to='explorer' replace />} />
      <Route path='*' element={<NotFound />} />
    </Route>,
  ),
);

function App() {
  return (
    <AppContainer>
      <TourRegistryProvider>
        <Suspense fallback={<FullscreenProgress isVisible />}>
          <RouterProvider router={router} />
        </Suspense>
      </TourRegistryProvider>
    </AppContainer>
  );
}

export default App;

const AppContainer = styled('div')`
  display: flex;
  height: 100vh;
`;

const ErrorState = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background: ${({ theme }) => theme.vars.palette.grey[100]};
`;

const ErrorCard = styled('div')`
  max-width: 520px;
  padding: 32px;
  border-radius: 16px;
  background: ${({ theme }) => theme.vars.palette.background.paper};
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.08);
`;

const ErrorTitle = styled('h1')`
  margin: 0 0 12px;
  font-size: 28px;
  line-height: 1.2;
`;

const ErrorText = styled('p')`
  margin: 0 0 12px;
  font-size: 16px;
  line-height: 1.5;
`;

const ErrorActions = styled('div')`
  margin-top: 24px;
`;

const ErrorButton = styled('button')`
  border: 0;
  border-radius: 999px;
  padding: 12px 20px;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.vars.palette.white};
  background: ${({ theme }) => theme.vars.palette.primary.main};
  cursor: pointer;
`;
